import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {OnCompileContext} from "#template/template-node/types"
import type {BuilderInlet, BuilderOutlet} from "#template/runtime-graph/graph-builder"
import type {MeshLikeNode} from "#template/nodes/object/mesh/fundamental"
import {meshLikeMarker} from "#template/nodes/object/mesh/fundamental"
import type {MaterialLikeNode} from "#template/nodes/material/fundamental"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {CompleteMeshData} from "#template/interfaces/mesh-data"
import type {MeshData, MeshNodeConstructorParameters, MeshNodeDefinition, MeshNodeParameters, MeshObjectData} from "#template/nodes/object/mesh/types"
import {ObjectNode} from "#template/nodes/object/object-node"
import {MaterialAssignment} from "#template/nodes/material-assignment/material-assignment"
import type {MaterialAssignmentConstructorParameters, MaterialAssignmentParameters} from "#template/nodes/material-assignment/material-assignment"
import type {IGetInfoNodeParts} from "#template/interactions"
import {DataObjectReference} from "#template/nodes/image/data-object-reference"
import type {ValueTypeDescriptor} from "#template/runtime-graph/descriptors"
import {GetMeshMetaData} from "#template/runtime-graph/nodes/get-mesh-meta-data"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {NodeClassImpl} from "#template/runtime-graph/types"
import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {NotReady} from "#template/runtime-graph/slots"
import {getRootTemplateNodeForObjectPath} from "#template/object-path-utils"
import {isIdNode} from "#template/nodes/id-node"
import type {ISceneManager} from "#template/interfaces/scene-manager"
import type {SceneNodes} from "#template/interfaces/scene-object"
import type {MeshRenderSettings} from "@cm/material-nodes"
import type {DataObject} from "@cm/utils"
import {deepEqual} from "@cm/utils"

const TD = TypeDescriptors

export const maxSubdivisionLimit = 5

export function getDisplaySubdivisionLevel(
    targetSubdivisionRenderIterations: number | undefined,
    scenePropertiesMaxSubdivisionLevelOnMobile: number | undefined,
    scenePropertiesMaxSubdivisionLevel: number | undefined,
    sceneManager: ISceneManager,
) {
    const maxSubdivisionLevel =
        sceneManager.isMobileDevice() || sceneManager.arMode()
            ? (scenePropertiesMaxSubdivisionLevelOnMobile ?? scenePropertiesMaxSubdivisionLevel)
            : scenePropertiesMaxSubdivisionLevel

    const defaultDeviceSubdivisionLimit = sceneManager.isMobileDevice() || sceneManager.arMode() ? 0 : maxSubdivisionLimit
    const displaySubdivLevel = Math.min(targetSubdivisionRenderIterations ?? 0, maxSubdivisionLevel ?? defaultDeviceSubdivisionLimit, maxSubdivisionLimit)

    return displaySubdivLevel
}

const MeshRenderSettingsDesc: ValueTypeDescriptor<MeshRenderSettings> = {
    deepCompare: (a, b) => {
        const jsonData = (meshRenderSettings: MeshRenderSettings) => {
            const {displacementImage, ...restMeshRenderSettings} = meshRenderSettings
            return {...restMeshRenderSettings, displacementImageHash: displacementImage?.imageNode.hash}
        }
        if (a && b) {
            return deepEqual(jsonData(a), jsonData(b))
        } else return a === b
    },
    deepCopy: (a) => {
        return {...a}
    },
}

const fallbackToEmptyRenderSettingsDescriptor = {
    input: TD.inlet(MeshRenderSettingsDesc),
    output: TD.outlet(MeshRenderSettingsDesc),
}

class FallbackToEmptyRenderSettings implements NodeClassImpl<typeof fallbackToEmptyRenderSettingsDescriptor, typeof FallbackToEmptyRenderSettings> {
    static descriptor = fallbackToEmptyRenderSettingsDescriptor
    static uniqueName = "FallbackToEmptyRenderSettings"
    input!: Inlet<MeshRenderSettings>
    output!: Outlet<MeshRenderSettings>

    run() {
        if (this.input === NotReady) {
            this.output.emitIfChanged({})
            return
        }

        this.output.emitIfChanged(this.input)
    }
}

export abstract class MeshNode<
        out Parameters extends MeshNodeParameters = MeshNodeParameters,
        out ConstructorParameters extends MeshNodeConstructorParameters = Omit<Parameters, "visible"> & {visible?: boolean},
    >
    extends ObjectNode<Parameters, ConstructorParameters>
    implements MeshLikeNode
{
    get [meshLikeMarker]() {
        return true as const
    }

    public abstract override get definition(): MeshNodeDefinition<Parameters, ConstructorParameters>

    public override evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<MeshObjectData | null> {
        const objectData = super.evaluate(scope, evaluator)
        const meshData = scope.unresolvedToNull(evaluator.templateScope.resolve<MeshData>(`meshData-${evaluator.getLocalId(this)}`))

        return scope.pureLambda(
            scope.tuple(objectData, meshData),
            ([objectData, meshData]) => {
                if (objectData === null || meshData === null) return null
                return {
                    ...objectData,
                    ...meshData,
                }
            },
            "evaluatedMesh",
        )
    }

    getDisplaySubdivisionLevel(context: OnCompileContext): number {
        return getDisplaySubdivisionLevel(
            this.parameters.subdivisionRenderIterations ?? 0,
            context.sceneProperties?.parameters.maxSubdivisionLevelOnMobile,
            context.sceneProperties?.parameters.maxSubdivisionLevel,
            context.evaluator.templateContext.sceneManager,
        )
    }

    getMaterialSlots(context?: IGetInfoNodeParts): Record<string, MaterialAssignmentParameters | null> {
        const materialAssignments = this.parameters.materialAssignments.parameters
        const result: Record<string, MaterialAssignmentParameters | null> = {}

        for (const slot in materialAssignments) {
            const assignment = materialAssignments[slot]
            if (assignment) result[slot] = assignment.parameters
            else result[slot] = null
        }

        return result
    }

    assignMaterial(slot: string, parameters: MaterialAssignmentConstructorParameters | null): void {
        this.parameters.materialAssignments.updateParameters({
            [slot]: parameters ? new MaterialAssignment({parameters}) : null,
        })
    }

    getMaterialSlotNames(): Record<string, string> | undefined {
        return this.parameters.materialSlotNames
    }

    setMaterialTransform(slot: string, horizontalOffset: number, verticalOffset: number, rotation: number): void {
        const materialAssignments = this.parameters.materialAssignments
        const assignmentNode = materialAssignments.parameters[slot]
        if (!assignmentNode) throw new Error(`Material assignment node not found for slot '${slot}'`)

        assignmentNode.updateParameters({
            horizontalOffset,
            verticalOffset,
            rotation,
        })
    }

    getMaterialForSlot(slot: string): MaterialLikeNode | undefined {
        const materialAssignments = this.parameters.materialAssignments
        const assignmentNode = materialAssignments.parameters[slot]
        return assignmentNode?.parameters.node ?? undefined
    }

    setupMesh(
        scope: GraphBuilderScope,
        context: OnCompileContext,
        completeMeshData: BuilderOutlet<CompleteMeshData>,
        isProcedural: boolean,
        invalidator?: BuilderOutlet<null>,
    ): void {
        const {evaluator} = context
        const {templateScope, templateContext} = evaluator
        const {sceneManager, lodType, computeDataObjectReferences} = templateContext

        const {
            displacementTexture,
            displacementUvChannel,
            displacementMin,
            displacementMax,
            displacementNormalStrength,
            displacementNormalSmoothness,
            displacementNormalOriginalResolution,
            visibleDirectly,
            visibleInReflections,
            visibleInRefractions,
            materialAssignments,
            receiveRealtimeShadows,
            castRealtimeShadows,
        } = this.parameters

        const displacementImage = scope.pureLambda(
            evaluator.evaluate(displacementTexture ?? null),
            (displacementImage) => {
                if (!displacementImage) return undefined
                return displacementImage
            },
            "displacementImageNode",
        )

        const meshData = scope.struct<MeshData>("MeshObjectData", {
            type: "mesh",
            completeMeshData,
            isProcedural,
            visibleDirectly,
            visibleInReflections,
            visibleInRefractions,
            receiveRealtimeShadows,
            castRealtimeShadows,
        })
        templateScope.alias(meshData, `meshData-${evaluator.getLocalId(this)}`)

        this.setupObject(
            scope,
            context,
            "Mesh",
            scope.get(scope.get(completeMeshData, "reified"), "bounds"),
            undefined,
            (objectProps, _otherData) => {
                const referencedDataObjects = (() => {
                    if (!computeDataObjectReferences) return undefined
                    const {meshMetaData} = scope.node(GetMeshMetaData, {sceneManager, lodType, completeMeshData})
                    const displacementDataObject =
                        displacementTexture instanceof DataObjectReference ? displacementTexture.getDataObject(scope, evaluator) : null
                    return scope.pureLambda(
                        scope.tuple(meshMetaData, objectProps.path, scope, displacementDataObject),
                        ([meshMetaData, path, scope, displacementDataObject]) => {
                            const result: SceneNodes.DataObjectReference[] = []

                            const idSuffix = new Map<string, number>()

                            const addDataObject = (dataObject: DataObject) => {
                                const id = `${scope.getPrefix()}:DataObjectReference@${dataObject.legacyId}`
                                const suffix = idSuffix.get(id)
                                const finalId = suffix === undefined ? id : `${id}-${suffix}`
                                idSuffix.set(id, (suffix ?? 0) + 1)

                                result.push({
                                    type: "DataObjectReference",
                                    id: finalId,
                                    path,
                                    dataObject,
                                    origin: {
                                        type: "mesh",
                                    },
                                })
                            }

                            for (const meshMetaDataItem of meshMetaData) {
                                const {resolvedData} = meshMetaDataItem
                                addDataObject(resolvedData)
                            }

                            if (displacementDataObject) {
                                addDataObject(displacementDataObject)
                            }

                            return result
                        },
                        `referencedDataObjects`,
                    )
                })()

                const meshRenderSettings = scope.value(
                    scope.struct<MeshRenderSettings>("MeshRenderSettings", {
                        displacementUvChannel,
                        displacementMin,
                        displacementMax,
                        displacementNormalStrength,
                        displacementNormalSmoothness,
                        displacementNormalOriginalResolution,
                        displacementImage,
                        // cryptoMatteObjectName: topLevelObjectId ?? objProps.id,
                        cryptoMatteAssetName: scope.pureLambda(
                            objectProps.path,
                            (path) => {
                                const topLevelObject = getRootTemplateNodeForObjectPath(path)
                                if (isIdNode(topLevelObject)) return topLevelObject.parameters.id
                                else return topLevelObject.instanceId
                            },
                            "cryptoMatteAssetName",
                        ),
                    }),
                    MeshRenderSettingsDesc,
                    "meshRenderSettings",
                )

                const {materialMap, preDisplayList} = materialAssignments.setupMaterialAssignments(scope, context, objectProps.path, meshRenderSettings)

                return {
                    displayList: scope.struct<SceneNodes.Mesh>("Mesh", {
                        type: "Mesh",
                        ...objectProps,
                        completeMeshData,
                        materialMap,
                        meshRenderSettings: scope.node(FallbackToEmptyRenderSettings, {input: meshRenderSettings}).output,
                        visibleDirectly,
                        visibleInReflections,
                        visibleInRefractions,
                        receiveRealtimeShadows,
                        castRealtimeShadows,
                        isDecal: false,
                        parent: null,
                        isProcedural,
                    }),
                    preDisplayList: referencedDataObjects
                        ? scope.flatten(scope.sparseList([preDisplayList, referencedDataObjects], "preDisplayList"))
                        : preDisplayList,
                }
            },
            invalidator,
        )
    }
}
